import type { ReactNode } from "react"
import {
	IconArrowDottedRotateAnticlockwiseOutline18,
	IconAtom2Outline18,
	IconBellOutline18,
	IconBookOpen4Outline18,
	IconChatBubbleClockOutline18,
	IconCircleDottedOutline18,
	IconCircleUserOutline18,
	IconDotsOutline18,
	IconLayers2Outline18,
	IconPlusOutline18,
	IconShapeCircleOutline18,
} from "nucleo-ui-outline-18"

import type { IconType } from "@productlane/ui"
import { cn } from "@productlane/lib/src/cn"
import { Badge } from "@productlane/ui"
import CustomChevron from "@productlane/ui/src/components/CustomChevron"

import { isDevEnvironment } from "@/env"
import {
	INBOX_NAV_COUNT,
	INBOX_NAV_GROUP,
	INBOX_NAV_ICON,
	INBOX_NAV_LEAD,
	INBOX_NAV_ROW,
	INBOX_NAV_ROW_ACTIVE,
	INBOX_NAV_ROW_INACTIVE,
} from "@/screens/app/inbox/components/inbox-nav-styles"
import { NotFound } from "@/screens/not-found"

export type LabelStyle = "today" | "caps" | "mono" | "rule"

interface Item {
	label: string
	icon: IconType
	count?: number
	active?: boolean
}

const TOP: Item[] = [
	{ label: "Notifications", icon: IconBellOutline18, count: 2 },
	{
		label: "Unassigned",
		icon: IconCircleDottedOutline18,
		count: 6,
		active: true,
	},
	{ label: "My inbox", icon: IconCircleUserOutline18, count: 8 },
]

const WORKSPACE: Item[] = [
	{
		label: "Close loop",
		icon: IconArrowDottedRotateAnticlockwiseOutline18,
		count: 12,
	},
	{ label: "All", icon: IconShapeCircleOutline18, count: 9 },
	{ label: "Agent", icon: IconAtom2Outline18, count: 4 },
	{ label: "Docs", icon: IconBookOpen4Outline18, count: 3 },
	{ label: "More", icon: IconDotsOutline18 },
]

const VIEWS: Item[] = [
	{ label: "Needs reply", icon: IconChatBubbleClockOutline18, count: 9 },
	{ label: "Enterprise", icon: IconLayers2Outline18 },
]

function Row({ item }: { item: Item }) {
	const Icon = item.icon
	return (
		<div
			className={cn(
				INBOX_NAV_ROW,
				item.active ? INBOX_NAV_ROW_ACTIVE : INBOX_NAV_ROW_INACTIVE,
			)}
		>
			<div className={INBOX_NAV_LEAD}>
				<Icon strokeWidth={1.25} className={INBOX_NAV_ICON} />
				<span className="truncate">{item.label}</span>
			</div>
			{item.count !== undefined && (
				<Badge variant="transparent" size="xs" className={INBOX_NAV_COUNT}>
					{item.count}
				</Badge>
			)}
		</div>
	)
}

/** Each label style for one section header; the chevron and plus wait for hover in all of them. */
export function SectionLabel({
	label,
	style,
	action,
}: {
	label: string
	style: LabelStyle
	action?: ReactNode
}) {
	const chevron = (
		<CustomChevron
			width={8}
			height={8}
			strokeWidth={2.25}
			className="flex-none rotate-90 transform-gpu opacity-0 group-hover/label:opacity-100"
		/>
	)
	const text = {
		today: "text-xs font-medium",
		caps: "text-[10.5px] font-semibold uppercase tracking-[0.07em]",
		mono: "font-mono text-[11px] lowercase",
		rule: "text-[11px] font-medium",
	}[style]

	return (
		<div className="group/label flex h-7 items-center gap-1 pl-2.5 pr-[9px] text-tertiary">
			<span className={cn("flex-none select-none", text)}>{label}</span>
			{chevron}
			{style === "rule" && (
				<span className="mx-1 h-px flex-1 bg-gray-200 dark:bg-gray-700/80" />
			)}
			<span className="flex-1" />
			{action}
		</div>
	)
}

export function SidebarMock({
	style,
	active,
}: {
	style: LabelStyle
	// Highlights this row instead of the default one.
	active?: string
}) {
	const mark = (item: Item): Item =>
		active === undefined ? item : { ...item, active: item.label === active }
	return (
		<div className="flex w-[240px] flex-none flex-col bg-gray-100 px-2.5 py-3 dark:bg-gray-950">
			<div className={INBOX_NAV_GROUP}>
				{TOP.map((item) => (
					<Row key={item.label} item={mark(item)} />
				))}
			</div>
			<div className={cn("mt-4", INBOX_NAV_GROUP)}>
				<SectionLabel label="Workspace" style={style} />
				{WORKSPACE.map((item) => (
					<Row key={item.label} item={mark(item)} />
				))}
			</div>
			<div className={cn("mt-4", INBOX_NAV_GROUP)}>
				<SectionLabel
					label="Views"
					style={style}
					action={
						<IconPlusOutline18
							strokeWidth={1}
							className="h-3.5 w-3.5 flex-none transform-gpu opacity-0 group-hover/label:opacity-100"
						/>
					}
				/>
				{VIEWS.map((item) => (
					<Row key={item.label} item={mark(item)} />
				))}
			</div>
		</div>
	)
}

export const VARIANTS: { id: LabelStyle; name: string; note: string }[] = [
	{
		id: "today",
		name: "Heute",
		note: "Zum Vergleich: 12px, Medium, normale Schreibweise. So macht es auch Linear.",
	},
	{
		id: "caps",
		name: "1. Versalien",
		note: "10,5px, Semibold, Großbuchstaben mit etwas Laufweite. Ruhiger und klar als Abschnitt erkennbar.",
	},
	{
		id: "mono",
		name: "2. Mono",
		note: "11px Monospace in Kleinbuchstaben. Passt zu einem Tool für Engineering-Teams.",
	},
	{
		id: "rule",
		name: "3. Mit Linie",
		note: "11px, Medium, und eine feine Linie läuft vom Wort bis zum Rand. Trennt die Abschnitte ohne zusätzlichen Abstand.",
	},
]

export function CombinedNavPage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<div className="min-h-screen overflow-x-auto bg-white p-8 dark:bg-gray-900">
			<div className="flex gap-8 pb-4">
				{VARIANTS.map((variant) => (
					<div
						key={variant.id}
						className="flex w-[240px] flex-none flex-col gap-3"
					>
						<div className="flex flex-col gap-1">
							<div className="text-sm font-semibold text-primary">
								{variant.name}
							</div>
							<div className="min-h-16 text-xs text-secondary">
								{variant.note}
							</div>
						</div>
						<div className="overflow-hidden rounded-xl ring-1 ring-black/5 dark:ring-white/10">
							<SidebarMock style={variant.id} />
						</div>
					</div>
				))}
			</div>
		</div>
	)
}
